import * as Yup from 'yup';

import { validateField, ValidationType } from './formValidation';
import type { AdditionalDetailsForm, CitizenshipInfo } from 'types';
import { shouldShowResidencyProof } from 'utils/residencyProof';

export const additionalDetailsDefaultValues: Partial<AdditionalDetailsForm> = {
    address: '',
    birth_date: '',
    city: '',
    country_of_residence: '',
    criminal_record: false,
    currency: '',
    driver_license: '',
    gender: '',
    income: 0,
    liquid_assets: 0,
    marital_status: '',
    net_worth: 0,
    postal_code: '',
    region_of_residence: '',
    residence_since: '',
    residency_status: '',
    has_residency_proof: null,
    previousVisa: [],
    class_2_with_airbrake: false,
};
// {
//     "address": null,
//     "birth_date": "1988-04-14",
//     "city": null,
//     "country_of_residence": "Cayman Islands",
//     "criminal_record": false,
//     "currency": "USD",
//     "driver_license": "1A",
//     "gender": "Female",
//     "income": 100000,
//     "liquid_assets": 3000000,
//     "marital_status": "Married",
//     "net_worth": 2000000,
//     "postal_code": null,
//     "region_of_residence": "Nova Scotia",
//     "residence_since": "2014-05-01",
//     "residency_status": "Citizen",
//     "user_id": 52
// }

export const previouslyHeldVisaSchema = Yup.array().of(
    Yup.object({
        name: validateField(ValidationType.STRING),
        start: validateField(ValidationType.STRING),
        end: validateField(ValidationType.STRING),
        country: validateField(ValidationType.STRING),
    })
);

export const additionalDetailValidationSchema = Yup.object({
    address: validateField(ValidationType.STRING, false),
    birth_date: validateField(ValidationType.STRING),
    // city: validateField(ValidationType.STRING).nullable(),
    country_of_residence: validateField(ValidationType.STRING),
    criminal_record: validateField(ValidationType.BOOLEAN),
    // currency: validateField(ValidationType.STRING),
    // driver_license: validateField(ValidationType.STRING),
    gender: validateField(ValidationType.STRING, false).oneOf(
        ['Male', 'Female', ''],
        'Please select a gender'
    ),
    income: Yup.number().min(0, 'Income must be positive').required('Income is required'),
    liquid_assets: Yup.number()
        .min(0, 'Liquid assets must be positive')
        .required('Liquid assets is required'),
    marital_status: validateField(ValidationType.STRING)
        .oneOf(['Single', 'Married'], 'Please select a marital status')
        .required('Marital status is required'),
    net_worth: Yup.number().min(0, 'Net worth must be positive').required('Net worth is required'),
    // postal_code: validateField(ValidationType.STRING).nullable(),
    // Region required only when living in a destination country (AU / CA / UK).
    region_of_residence: Yup.string()
        .nullable()
        .when('country_of_residence', {
            is: (country: string) =>
                ['Australia', 'Canada', 'United Kingdom'].includes(country || ''),
            then: (schema) => schema.required('Region is required'),
            otherwise: (schema) => schema.nullable(),
        }),
    residence_since: validateField(ValidationType.STRING),
    residency_status: validateField(ValidationType.STRING),
    has_residency_proof: Yup.boolean().nullable(),
    class_2_with_airbrake: validateField(ValidationType.BOOLEAN),
    previousVisa: previouslyHeldVisaSchema,
});

export const getAdditionalDetailValidationSchema = (
    isCanadianPlatform: boolean,
    citizenships: readonly Pick<CitizenshipInfo, 'country'>[] | null | undefined,
    requiredMessage = 'This is a required field'
) =>
    additionalDetailValidationSchema.shape({
        has_residency_proof: Yup.boolean()
            .nullable()
            .when('country_of_residence', {
                is: (country: string | null | undefined) =>
                    shouldShowResidencyProof(isCanadianPlatform, country, citizenships),
                then: (schema) => schema.required(requiredMessage),
            }),
    });
